import { Stack, Typography } from '@mui/material';

import { formatPeriod, formatPeriods } from 'modules/common/components/VerifiedStatementUpload/utils/formatMonthList';
import Dialog from 'modules/common/components/MUI/Dialog';
import { StatementPeriod } from 'services/bookkeeping/queries/useStatementCoverage';

interface ConfirmPeriodMatchDialogProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly onConfirm: () => void;
    readonly fileName: string;
    /** The month the member picked. */
    readonly period: StatementPeriod;
    /** What our own reading said this file was for, when it read anything at all. */
    readonly believedPeriods: StatementPeriod[];
}

/**
 * Confirms a match the member is making that we did not make ourselves. Only shown when the
 * chosen month isn't one we read — asking when we agree would train them to click through it.
 * Worded per case: we read a different month (named, since that is the likeliest sign of a
 * wrong row) or nothing usable. Not a blocker; it makes overriding a decision, not a slip.
 */
export default function ConfirmPeriodMatchDialog({
    open,
    onClose,
    onConfirm,
    fileName,
    period,
    believedPeriods,
}: Readonly<ConfirmPeriodMatchDialogProps>) {
    const periodLabel = formatPeriod(period);
    const believedLabel = formatPeriods(believedPeriods);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            heading={`Match this to ${periodLabel}?`}
            size="small"
            negativeAction={{ label: 'Go back', onClick: onClose }}
            positiveAction={{ label: 'Yes, match it', onClick: onConfirm }}>
            <Stack spacing={2}>
                <Typography variant="body1">
                    {believedLabel
                        ? `We read ${fileName} as your ${believedLabel} statement, not ${periodLabel}.`
                        : `We couldn't read ${periodLabel} from ${fileName}.`}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    If you&apos;re sure this is the right statement for {periodLabel}, we&apos;ll label it that way and
                    check it when we prepare your books.
                </Typography>
            </Stack>
        </Dialog>
    );
}
